import React, { useState, useEffect } from 'react';
import { toast } from 'react-toastify';
import { Shield, Save, ShoppingCart } from 'lucide-react';
import axios from 'axios';

const API_URL = process.env.REACT_APP_API_URL || '/api';

const EmployeePermissions = () => {
  const [employees, setEmployees] = useState([]);
  const [permissions, setPermissions] = useState({});
  const [globalSettings, setGlobalSettings] = useState({ quickBuyEnabled: true });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    loadEmployees();
    loadPermissions();
  }, []);

  const loadEmployees = async () => {
    try {
      const token = sessionStorage.getItem('token');
      const response = await axios.get(`${API_URL}/admin/users`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      // Filter to only show employees/groomers (not admins or clients)
      const employeeList = response.data.filter(user => 
        user.role === 'groomer' || user.role === 'employee'
      );
      setEmployees(employeeList);
    } catch (error) {
      console.error('Load employees error:', error);
      console.warn('Employees not loaded yet');
    }
  };

  const loadPermissions = async () => {
    try {
      const token = sessionStorage.getItem('token');
      const response = await axios.get(`${API_URL}/business-info/employee_permissions`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      if (response.data.info_value) {
        const data = JSON.parse(response.data.info_value);
        setGlobalSettings(data.global || { quickBuyEnabled: true });
        setPermissions(data.individual || {});
      }
    } catch (error) {
      console.error('Load permissions error:', error);
      // Keep empty permissions if not found
    } finally {
      setLoading(false);
    }
  };

  const handleTogglePermission = (userId, permission) => {
    setPermissions(prev => ({
      ...prev,
      [userId]: {
        ...prev[userId],
        [permission]: !prev[userId]?.[permission]
      }
    }));
  };

  const handleToggleGlobalQuickBuy = () => {
    setGlobalSettings(prev => ({
      ...prev,
      quickBuyEnabled: !prev.quickBuyEnabled
    }));
  };

  const handleSavePermissions = async () => {
    setSaving(true);
    try {
      const token = sessionStorage.getItem('token');
      const data = {
        global: globalSettings,
        individual: permissions
      };
      const permissionsString = JSON.stringify(data);

      await axios.put(
        `${API_URL}/business-info/employee_permissions`,
        { value: permissionsString },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      toast.success('Employee permissions updated successfully!');
      
      // Trigger navbar refresh for all users
      window.dispatchEvent(new Event('permissionsUpdated'));
    } catch (error) {
      console.error('Save permissions error:', error);
      toast.error('Failed to save permissions');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-8">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  return (
    <div className="card">
      <div className="flex items-center space-x-3 mb-4">
        <Shield className="text-primary-600" size={24} />
        <h2 className="text-xl font-bold text-gray-900">Employee Permissions</h2>
      </div>

      <p className="text-gray-600 mb-6">
        Control which features employees can access. Use the global toggle to enable/disable Quick Buy for all employees, 
        then selectively enable it for specific employees.
      </p>

      {/* Global Quick Buy Toggle */}
      <div className="bg-gradient-to-r from-blue-50 to-indigo-50 border-2 border-blue-200 rounded-lg p-5 mb-6">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-3">
            <ShoppingCart className="text-blue-600" size={24} />
            <div>
              <h3 className="font-bold text-gray-900">Quick Buy Feature</h3>
              <p className="text-sm text-gray-600">
                {globalSettings.quickBuyEnabled 
                  ? 'Quick Buy is enabled. You can now grant access to individual employees below.' 
                  : 'Quick Buy is disabled for all employees. Enable it to grant individual access.'}
              </p>
            </div>
          </div>
          <label className="relative inline-flex items-center cursor-pointer">
            <input
              type="checkbox"
              checked={globalSettings.quickBuyEnabled}
              onChange={handleToggleGlobalQuickBuy}
              className="sr-only peer"
            />
            <div className="w-14 h-7 bg-gray-300 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-0.5 after:left-[4px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-6 after:w-6 after:transition-all peer-checked:bg-blue-600"></div>
          </label>
        </div>
      </div>

      {employees.length === 0 ? (
        <div className="text-center py-8 text-gray-500">
          No employees found. Add employees in the Users section.
        </div>
      ) : (
        <div className="space-y-4">
          {employees.map(employee => (
            <div key={employee.id} className="bg-gray-50 p-4 rounded-lg border border-gray-200">
              <div className="flex items-center justify-between mb-3">
                <div>
                  <h3 className="font-semibold text-gray-900">
                    {employee.first_name} {employee.last_name}
                  </h3>
                  <p className="text-sm text-gray-600">{employee.email}</p>
                  <span className="inline-block mt-1 px-2 py-1 text-xs bg-blue-100 text-blue-800 rounded">
                    {employee.role === 'groomer' ? 'Groomer' : 'Employee'}
                  </span>
                </div>
              </div>

              <div className="space-y-2">
                {/* Quick Buy Permission */}
                <label className={`flex items-center space-x-3 p-3 bg-white rounded border border-gray-200 transition-colors ${
                  globalSettings.quickBuyEnabled 
                    ? 'hover:border-primary-300 cursor-pointer' 
                    : 'opacity-50 cursor-not-allowed'
                }`}>
                  <input
                    type="checkbox"
                    checked={permissions[employee.id]?.quickBuy || false}
                    onChange={() => handleTogglePermission(employee.id, 'quickBuy')}
                    disabled={!globalSettings.quickBuyEnabled}
                    className="w-5 h-5 text-primary-600 rounded focus:ring-primary-500 disabled:opacity-50 disabled:cursor-not-allowed"
                  />
                  <div className="flex items-center space-x-2 flex-1">
                    <ShoppingCart size={18} className="text-gray-600" />
                    <div>
                      <p className="font-medium text-gray-900">Quick Buy Access</p>
                      <p className="text-xs text-gray-600">
                        {globalSettings.quickBuyEnabled 
                          ? 'Allow employee to access Quick Buy for in-person sales' 
                          : 'Enable Quick Buy globally first to grant individual access'}
                      </p>
                    </div>
                  </div>
                </label>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Save Button */}
      <div className="mt-6 flex justify-end">
        <button
          onClick={handleSavePermissions}
          disabled={saving || employees.length === 0}
          className="btn btn-primary flex items-center space-x-2"
        >
          <Save size={18} />
          <span>{saving ? 'Saving...' : 'Save Permissions'}</span>
        </button>
      </div>
    </div>
  );
};

export default EmployeePermissions;
